import { useEffect } from 'react';

function getPageScrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    const canScrollY =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (canScrollY && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return (
    document.querySelector('.dashboard-content') ||
    document.scrollingElement ||
    document.documentElement
  );
}

/**
 * Nested overflow lists on iOS/Android PWA often trap touch gestures.
 * When the list is at a scroll edge (or does not overflow), forward the
 * gesture to the dashboard page scroller so page scroll keeps working.
 */
export function useNestedScrollChain(ref, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const el = ref?.current;
    if (!el) return undefined;

    let lastY = 0;

    const onTouchStart = (event) => {
      if (!event.touches.length) return;
      lastY = event.touches[0].clientY;
    };

    const onTouchMove = (event) => {
      if (!event.touches.length) return;

      const y = event.touches[0].clientY;
      const deltaY = lastY - y;
      lastY = y;

      if (deltaY === 0) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = Math.max(0, scrollHeight - clientHeight);
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop >= maxScroll - 1;

      // Past the edge (or list does not overflow) → scroll the page instead
      const pastTop = atTop && deltaY < 0;
      const pastBottom = atBottom && deltaY > 0;
      if (!pastTop && !pastBottom) return;

      const page = getPageScrollParent(el);
      if (!page || page === el) return;

      page.scrollTop += deltaY;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [ref, enabled]);
}
